import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw, Search, ArrowLeftRight } from 'lucide-react';
import useWalletTransactions from '../hooks/useWalletTransactions';
import { formatINR } from '../utils/formatINR';
import { DateTime, Field, MessageRow, Money, Mono, PageHeader, PageShell, TableCard, Td, Th, Toolbar, rowClass, tableClass, tbodyClass, theadClass } from '../components/ui/DataPage';

// display only: the transaction time on two lines (date, then time)
const dayFormatter = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const clockFormatter = new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
const formatWhen = (raw, fallback) => {
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return { date: fallback || '', time: '' };
  return { date: dayFormatter.format(d), time: clockFormatter.format(d) };
};

export default function WalletTransactionsPage() {
  const [dateRange, setDateRange] = useState({ fromDate: '', toDate: '' });
  const { transactions, isLoading, error, message, refetch } = useWalletTransactions(dateRange);
  const [localTransactions, setLocalTransactions] = useState([]);

  // Show server message as toast
  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  // Normalize server data
  useEffect(() => {
    if (transactions) {
      const cleaned = transactions.map(tx => ({
        id: tx.ID,
        amount: tx.Amount,
        created_at: tx.CreatedAt ? new Date(tx.CreatedAt).toLocaleString() : '',
        created_at_raw: tx.CreatedAt || '',
        modified_at: tx.ModifiedAt ? new Date(tx.ModifiedAt).toLocaleString() : '',
        user_id: tx.UserID,
        wallet_id: tx.WalletID || '',
        transaction_type: tx.TransactionType || '',
        payment_method: tx.PaymentMethod || '',
        reference_id: tx.ReferenceID || '',
      }));
      setLocalTransactions(cleaned);
    }
  }, [transactions]);

  const [search, setSearch] = useState('');

  // Filter by user_id
  const filteredTransactions = localTransactions.filter(tx =>
    (!search || tx.user_id.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <PageShell>
      {/* Header */}
      <PageHeader
        icon={ArrowLeftRight}
        title="Wallet Transactions"
        description="Search wallet transactions by user and date range"
        actions={
          <button onClick={() => refetch()} className="btn-secondary">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Filters */}
      <Toolbar>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {/* Search by User ID */}
          <Field label="User ID" className="sm:min-w-[240px] sm:flex-1">
            <span className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search by User ID"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input w-full pl-9"
              />
            </span>
          </Field>

          {/* From Date */}
          <Field label="Start date">
            <input
              type="date"
              value={dateRange.fromDate}
              onChange={e => setDateRange(prev => ({ ...prev, fromDate: e.target.value }))}
              className="input w-full sm:w-[160px]"
            />
          </Field>

          {/* To Date */}
          <Field label="End date">
            <input
              type="date"
              value={dateRange.toDate}
              onChange={e => setDateRange(prev => ({ ...prev, toDate: e.target.value }))}
              className="input w-full sm:w-[160px]"
            />
          </Field>

          <button onClick={() => refetch()} className="btn-primary sm:px-5">
            Apply
          </button>
        </div>
      </Toolbar>

      {/* Transactions Table */}
      <TableCard
        header={
          <span>
            <span className="font-semibold tabular-nums text-brand-950">{filteredTransactions.length}</span> transactions
          </span>
        }
      >
        <table className={tableClass}>
          <thead className={theadClass}>
            <tr>
              <Th>Date</Th>
              <Th>User ID</Th>
              <Th align="right">Amount</Th>
            </tr>
          </thead>
          <tbody className={tbodyClass}>
            {filteredTransactions.map(tx => {
              const when = formatWhen(tx.created_at_raw, tx.created_at);
              return (
                <tr key={tx.id} className={rowClass}>
                  <Td>
                    <DateTime date={when.date} time={when.time} />
                  </Td>
                  <Td>
                    <Mono max="max-w-[140px] sm:max-w-[320px]">{tx.user_id}</Mono>
                  </Td>
                  <Td align="right">
                    <Money>{formatINR(tx.amount)}</Money>
                  </Td>
                </tr>
              );
            })}

            {filteredTransactions.length === 0 && !isLoading && <MessageRow colSpan={3}>No transactions found</MessageRow>}

            {isLoading && <MessageRow colSpan={3}>Loading...</MessageRow>}

            {error && <MessageRow colSpan={3} tone="error">{error}</MessageRow>}
          </tbody>
        </table>
      </TableCard>
    </PageShell>
  );
}
